import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    List, ShieldCheck, Key, SignOut, SpeakerHigh, DeviceMobile, Bell, Circle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Avatar } from "@/components/Avatar";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationSoundPref, previewNotifBeep } from "@/hooks/useNotificationSoundPref";
import { unlockAudio } from "@/lib/notificationSound";
import { usePushSubscription } from "@/hooks/usePushSubscription";

const STATUS_LABEL = {
    connected: "Conectado",
    connecting: "Conectando…",
    disconnected: "Sin conexión",
};
const STATUS_COLOR = {
    connected: "bg-emerald-500",
    connecting: "bg-amber-400",
    disconnected: "bg-slate-400",
};

/**
 * Hamburger-drawer companion for the mobile Level-1 (list) view.
 *
 * Mirrors the desktop user-dropdown menu items 1-for-1 (change password,
 * sound toggle, push toggle, admin link, sign out) but arranged as a
 * full-height Sheet from the left, which is more thumb-friendly than a
 * cramped dropdown on a 375-px viewport.
 *
 * Every action closes the drawer BEFORE firing so the user sees the app
 * update immediately (toasts, navigation, dialog opens). Toggles are the
 * exception — they stay open so the operator can flip multiple switches
 * in one session without re-opening the drawer.
 */
export default function MobileMenuDrawer({ open, onOpenChange, wsStatus = "disconnected" }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [soundEnabled, setSoundEnabled] = useNotificationSoundPref();
    const push = usePushSubscription();
    const [changePwdOpen, setChangePwdOpen] = useState(false);

    const close = () => onOpenChange(false);

    const goto = (path) => {
        close();
        // Slight delay so the drawer close animation doesn't fight the route
        // change animation — reads as a smooth transition on the phone.
        setTimeout(() => navigate(path), 60);
    };

    const onLogout = async () => {
        close();
        try { await logout(); }
        catch { /* AuthContext handles errors — just close */ }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    side="left"
                    className="w-[86vw] sm:w-[380px] p-0 flex flex-col"
                    data-testid="mobile-menu-drawer"
                >
                    {/* Header with app brand. The Sheet's built-in close (top-right X)
                        handles closing — no need for a duplicate button here. */}
                    <div className="px-5 pt-6 pb-4 border-b border-slate-100 vh-safe-top flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-brand-600 text-white font-display font-black text-lg flex items-center justify-center">
                            V
                        </div>
                        <div className="font-display font-bold text-lg text-slate-900">ViajaHub</div>
                    </div>

                    {/* User card */}
                    <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                        <Avatar name={user?.name} color={user?.avatar_color} size={44} />
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900 truncate">{user?.name}</div>
                            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">
                                {user?.role}
                            </div>
                        </div>
                    </div>

                    {/* WS status */}
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm">
                        <Circle size={9} weight="fill" className={STATUS_COLOR[wsStatus]?.replace("bg-", "text-") || "text-slate-400"} />
                        <span className="text-slate-600">{STATUS_LABEL[wsStatus] || STATUS_LABEL.disconnected}</span>
                    </div>

                    {/* Nav actions */}
                    <div className="flex-1 overflow-y-auto vh-mobile-scroll py-2">
                        {user?.role === "admin" && (
                            <button
                                type="button"
                                onClick={() => goto("/admin/users")}
                                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                                data-testid="mobile-menu-admin"
                            >
                                <ShieldCheck size={20} className="text-slate-600" />
                                <span className="flex-1 text-sm font-medium text-slate-800">Admin</span>
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={() => { close(); setTimeout(() => setChangePwdOpen(true), 100); }}
                            className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                            data-testid="mobile-menu-change-password"
                        >
                            <Key size={20} className="text-slate-600" />
                            <span className="flex-1 text-sm font-medium text-slate-800">Cambiar contraseña</span>
                        </button>

                        {/* Sound toggle */}
                        <div
                            className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors cursor-pointer"
                            onClick={() => {
                                const next = !soundEnabled;
                                setSoundEnabled(next);
                                if (next) {
                                    unlockAudio();
                                    previewNotifBeep();
                                    toast.success("Sonido activado");
                                } else {
                                    toast.message("Sonido silenciado");
                                }
                            }}
                            data-testid="mobile-menu-sound-toggle"
                        >
                            <SpeakerHigh size={20} className="text-slate-600" />
                            <span className="flex-1 text-sm font-medium text-slate-800">Sonido de notificaciones</span>
                            <Switch checked={soundEnabled} className="pointer-events-none" onCheckedChange={() => {}} />
                        </div>

                        {/* Push toggle */}
                        {push.supported ? (
                            <div
                                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors cursor-pointer"
                                onClick={async () => {
                                    if (push.loading) return;
                                    if (push.subscribed) {
                                        const res = await push.unsubscribe();
                                        if (res.ok) toast.message("Notificaciones push desactivadas");
                                        else toast.error(res.error || "No se pudo desactivar");
                                    } else if (push.permission === "denied") {
                                        toast.error(
                                            "El navegador bloqueó las notificaciones. Habilítalas desde ajustes del sistema.",
                                            { duration: 6000 },
                                        );
                                    } else {
                                        const res = await push.subscribe();
                                        if (res.ok) toast.success("Notificaciones push activadas");
                                        else toast.error(res.error || "No se pudo activar");
                                    }
                                }}
                                data-testid="mobile-menu-push-toggle"
                            >
                                <DeviceMobile size={20} className="text-slate-600" />
                                <span className="flex-1 text-sm font-medium text-slate-800">Notificaciones push</span>
                                <Switch checked={push.subscribed} disabled={push.loading} className="pointer-events-none" onCheckedChange={() => {}} />
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => toast.message(
                                    "En iPhone/iPad, instalá ViajaHub en la pantalla principal (menú Compartir → Añadir a inicio) para recibir notificaciones push.",
                                    { duration: 8000 },
                                )}
                                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                                data-testid="mobile-menu-push-unsupported"
                            >
                                <DeviceMobile size={20} className="text-slate-400" />
                                <span className="flex-1 text-sm text-slate-500">Push no disponible</span>
                                <Bell size={14} className="text-slate-400" />
                            </button>
                        )}
                    </div>

                    {/* Logout — pinned to the bottom */}
                    <div className="border-t border-slate-100 vh-safe-bottom">
                        <button
                            type="button"
                            onClick={onLogout}
                            className="w-full px-5 py-4 flex items-center gap-3 hover:bg-rose-50 active:bg-rose-100 transition-colors text-left text-rose-700"
                            data-testid="mobile-menu-logout"
                        >
                            <SignOut size={20} />
                            <span className="flex-1 text-sm font-semibold">Cerrar sesión</span>
                        </button>
                    </div>
                </SheetContent>
            </Sheet>

            <ChangePasswordDialog
                open={changePwdOpen}
                onOpenChange={setChangePwdOpen}
                forced={user?.force_password_reset === true}
            />
        </>
    );
}

/**
 * Small hamburger trigger button. Kept as a named export so the mobile
 * layout can drop it into whatever header it needs — the drawer state
 * lives in the parent.
 */
export function MobileMenuTrigger({ onClick, notifCount = 0 }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="vh-tappable rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center relative"
            aria-label="Abrir menú"
            data-testid="mobile-menu-trigger"
        >
            <List size={22} weight="regular" />
            {notifCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 text-[9px] font-bold bg-brand-600 text-white rounded-full flex items-center justify-center">
                    {notifCount > 9 ? "9+" : notifCount}
                </span>
            )}
        </button>
    );
}
